import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Row,
  Col,
  Modal,
  Button,
  Upload,
  Icon,
  Input,
  message,
  Spin,
} from 'antd';
import ReactSelect from 'react-select';
import SimpleReactValidator from 'simple-react-validator';
import { SpH5 } from 'components/DesignKit';

type Props = {
  lynxConnectVendor: Array,
  lynxConnectLocations: Array,
  addFileModalVisibility: Boolean,
  lynxConnectAddNewInvoiceStatus: String,
  setAddFileModalVisibility: Boolean,
  debouncedAddNewLynxConnectInvoice: Function,
  resetAddNewInvoiceStatus: Function,
  setUploadSuccessModalVisibility: Function,
  lynxConnectLoading: Boolean,
};

const AddNewInvoiceModal = (props: Props) => {
  const {
    lynxConnectVendor,
    lynxConnectLocations,
    addFileModalVisibility,
    lynxConnectAddNewInvoiceStatus,
    setAddFileModalVisibility,
    debouncedAddNewLynxConnectInvoice,
    resetAddNewInvoiceStatus,
    setUploadSuccessModalVisibility,
    lynxConnectLoading,
  } = props;

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedVendorName, setSelectedVendorName] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileUploadStatus, setFileUploadStatus] = useState(false);
  const [isUploadFileNextEnabled, setIsUploadFileNextEnabled] = useState(false);
  const [fileUploadList, setFileUploadList] = useState([]);
  const validator = useRef(new SimpleReactValidator());
  const [, forceStateUpdate] = useState(0);

  // enable next button only when the values have been set
  useEffect(() => {
    if (!selectedLocation
      || !selectedVendorName
      || !fileUploadStatus
      || !fileName
      || fileName.length <= 3
    ) {
      setIsUploadFileNextEnabled(false);
      return;
    }
    setIsUploadFileNextEnabled(true);
  }, [selectedLocation,
    selectedVendorName,
    fileUploadStatus,
    fileName,
  ]);

  const handleUploadFileModalCancel = () => {
    if (lynxConnectLoading) {
      return;
    }
    validator.current.hideMessages();
    // clear the fields
    setFileUploadList([]);
    setSelectedLocation('');
    setSelectedVendorName('');
    setFileName('');
    setAddFileModalVisibility(false);
  };

  const handleUploadFileModalSave = () => {
    if (validator.current.allValid()) {
      // call api to upload the file and details
      // these are primarily for location, vendor and filename info
      const fileData = fileUploadList.length === 1 ? fileUploadList[0].originFileObj : null;
      // rename the fileData's filename
      let santizedFileName = fileName.trim();
      if (santizedFileName.slice(-4).toLowerCase() !== '.pdf') {
        santizedFileName += '.pdf';
      }
      const renamedFileData = new File([fileData], santizedFileName, {
        type: fileData.type,
        lastModified: fileData.lastModified,
      });
      debouncedAddNewLynxConnectInvoice({
        selectedLocationGid: selectedLocation?.value,
        lynxConnectVendorGid: selectedVendorName?.value,
        fileName: santizedFileName,
        fileData: renamedFileData,
      });
      validator.current.hideMessages();
    } else {
      validator.current.showMessages();
      forceStateUpdate(state => (state === 0 ? state + 1 : state - 1));
    }
    setIsUploadFileNextEnabled(false); // disable it as it is being submitted
  };

  // when the invoice add is successful,
  // hide the file modal and show upload successful
  // else hide the file upload modal alone
  useEffect(() => {
    if (lynxConnectAddNewInvoiceStatus === 'Success') {
      setUploadSuccessModalVisibility(true);
      resetAddNewInvoiceStatus('');
    } else if (lynxConnectAddNewInvoiceStatus === 'Error') {
      resetAddNewInvoiceStatus('');
    }
    setAddFileModalVisibility(false);
    // clear the fields
    setFileUploadList([]);
    setSelectedLocation('');
    setSelectedVendorName('');
    setFileName('');
    // clear the file buffer data too
  }, [lynxConnectAddNewInvoiceStatus]);

  // upload props, need to set properly, depends on the backend
  const uploadProps = {
    name: 'file',
    fileList: fileUploadList,
    beforeUpload: () => false,
    onRemove: () => {
      setFileUploadStatus(false);
      setFileUploadList([]);
    },
    onChange: ({ fileList }) => {
      // check file type and size
      const file = fileList.slice(-1)[0];
      if (!file) {
        message.error('No File found.');
        setFileUploadStatus(false);
        return;
      }
      const isLt10M = (file?.size ?? 0) / 1024 / 1024 < 10;
      const pdfFormat = 'application/pdf';
      const pattern = file.type.match(pdfFormat);
      if (!pattern) {
        message.error('Only PDF files are allowed.');
        return;
      }
      if (pattern && !isLt10M) {
        message.error('File must be smaller than 10MB!');
        return;
      }
      // set the necessary states for valid files
      setFileUploadList(fileList.slice(-1));
      setFileUploadStatus(true);
      setFileName(fileList.slice(-1)[0]?.name);
    },
  };

  return (
    <Modal
      title="Scan"
      visible={addFileModalVisibility}
      onCancel={handleUploadFileModalCancel}
      // onOk={handleUploadFileModalSave}
      footer={[
        <Button
          key="cancel"
          onClick={handleUploadFileModalCancel}
          disabled={lynxConnectLoading}
        >
          Cancel
        </Button>,
        <Button
          key="next"
          type="primary"
          onClick={handleUploadFileModalSave}
          disabled={!isUploadFileNextEnabled}
        >
          Next
        </Button>,
      ]}
      centered
      maskClosable={!lynxConnectLoading}
      closable={!lynxConnectLoading}
    >
      <Spin
        spinning={lynxConnectLoading}
        tip="Please wait..."
        size="large"
        delay={300} // delay in ms to avoid flicker
      >
        <div style={{ padding: '16px' }}>
          <Row gutter={16}>
            <Col span={24}>
              <label>
                <SpH5>Choose a Location</SpH5>
              </label>
              <ReactSelect
                name="lynxConnectNewInvoiceChooseLocation"
                options={lynxConnectLocations}
                value={selectedLocation}
                onChange={(option) => setSelectedLocation(option)}
                placeholder="Select a location"
                isClearable
                isSearchable
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                }}
                filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
              />
              <section style={{ color: 'red' }}>
                { validator.current.message('lynxConnectNewInvoiceChooseLocation', selectedLocation, 'required') }
              </section>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <label>
                <SpH5>Vendor Name</SpH5>
              </label>
              {/* <Input
                name="lynxConnectNewInvoiceChooseVendor"
                placeholder="Enter the Vendor Name"
                value={selectedVendorName}
                onChange={(e) => setSelectedVendorName(e.target.value)}
              /> */}
              <ReactSelect
                name="lynxConnectNewInvoiceChooseVendor"
                options={lynxConnectVendor}
                value={selectedVendorName}
                onChange={(option) => setSelectedVendorName(option)}
                placeholder="Select a Vendor Name"
                isClearable
                isSearchable
                styles={{
                  container: (base) => ({ ...base, width: '100%' }),
                }}
                filterOption={(option, inputValue) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
              />
              <section style={{ color: 'red' }}>
                { validator.current.message('lynxConnectNewInvoiceChooseVendor', selectedVendorName, 'required') }
              </section>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={6}>
              <label>
                <SpH5>Upload File</SpH5>
              </label>
            </Col>
            <Col span={18}>
              <Upload
                {...uploadProps}
                name="lynxConnectNewInvoiceCUploadFile"
              >
                <Button>
                  <Icon type="upload" /> Upload
                </Button>
              </Upload>
              <section style={{ color: 'red' }}>
                { validator.current.message('lynxConnectNewInvoiceCUploadFile', fileUploadList.length === 1 ? 'ok' : '', 'required') }
              </section>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col span={24}>
              <label>
                <SpH5>File Name</SpH5>
              </label>
              <Input
                name="lynxConnectNewInvoiceFilename"
                placeholder="Enter the Filename"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
              <section style={{ color: 'red' }}>
                { validator.current.message('lynxConnectNewInvoiceFilename', fileName, 'required') }
              </section>
            </Col>
          </Row>
        </div>
      </Spin>
    </Modal>
  );
};

export default AddNewInvoiceModal;
